/**
 * Emissions Business Metrics Page
 *
 * Manage business metrics (employee count, revenue, floor area) for carbon intensity calculations.
 * Supports multiple periods to track changes over time.
 */

import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ArrowLeft, Info, Users, DollarSign, Building2, Loader2, Pencil, Trash2, PieChart, Save } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { Dashboard } from '@g17eco/ui-modern/components/ui/dashboard';
import { generateUrl } from '@routes/util';
import { emissionsTrackerRoutes } from '../routes';
import {
  useGetBusinessMetricsListQuery,
  useUpdateBusinessMetricsMutation,
  useDeleteBusinessMetricsMutation,
} from '../api/emissions';
import {
  BusinessMetrics,
  BusinessMetricsRequest,
  FloorAreaUnit,
  EmployeeCountType,
} from '../types/emissions';

const selectClass = 'tw:flex tw:h-9 tw:w-full tw:rounded-md tw:border tw:border-input tw:bg-transparent tw:px-3 tw:py-1 tw:text-sm tw:shadow-sm tw:outline-none focus-visible:tw:ring-1 focus-visible:tw:ring-ring tw:cursor-pointer';
const inputClass = 'tw:flex tw:h-9 tw:w-full tw:rounded-md tw:border tw:border-input tw:bg-transparent tw:px-3 tw:py-1 tw:text-sm tw:shadow-sm tw:outline-none focus-visible:tw:ring-1 focus-visible:tw:ring-ring';

const getYear = (dateString: string): number => {
  return new Date(dateString).getFullYear();
};

const formatNumber = (value: number | undefined): string => {
  if (value === undefined) {
    return '--';
  }
  return value.toLocaleString();
};

const formatRevenue = (value: number | undefined, currency: string | undefined): string => {
  if (value === undefined) {
    return '--';
  }
  const currencySymbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : '$';
  const millions = value / 1_000_000;
  if (millions >= 1) {
    return `${currencySymbol}${millions.toFixed(1)}M`;
  }
  const thousands = value / 1_000;
  if (thousands >= 1) {
    return `${currencySymbol}${thousands.toFixed(0)}K`;
  }
  return `${currencySymbol}${value.toLocaleString()}`;
};

const formatFloorArea = (value: number | undefined, unit: FloorAreaUnit | undefined): string => {
  if (value === undefined) {
    return '--';
  }
  const unitLabel = unit === FloorAreaUnit.SquareFeet ? 'ft²' : 'm²';
  return `${value.toLocaleString()} ${unitLabel}`;
};

interface MetricsFormProps {
  metrics?: BusinessMetrics | null;
  existingYears: number[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: BusinessMetricsRequest) => Promise<void>;
  isSaving: boolean;
}

const MetricsForm = ({ metrics, existingYears, isOpen, onClose, onSave, isSaving }: MetricsFormProps) => {
  const isEdit = !!metrics;
  const currentYear = new Date().getFullYear();
  const editingYear = metrics?.periodStart ? getYear(metrics.periodStart) : null;

  const allYears = Array.from({ length: 8 }, (_, i) => currentYear - 5 + i);
  const availableYears = allYears.filter(
    (year) => !existingYears.includes(year) || year === editingYear
  );

  const getDefaultYear = () => {
    if (editingYear) {
      return editingYear;
    }
    if (availableYears.includes(currentYear)) {
      return currentYear;
    }
    return availableYears[availableYears.length - 1] ?? currentYear;
  };

  const getDefaultFormData = (): Omit<BusinessMetricsRequest, 'periodStart' | 'periodEnd'> => ({
    employeeCount: metrics?.employeeCount,
    employeeCountType: metrics?.employeeCountType ?? EmployeeCountType.FTE,
    revenue: metrics?.revenue,
    revenueCurrency: metrics?.revenueCurrency ?? 'USD',
    floorArea: metrics?.floorArea,
    floorAreaUnit: metrics?.floorAreaUnit ?? FloorAreaUnit.SquareMeters,
    notes: metrics?.notes ?? '',
    dataSource: metrics?.dataSource ?? 'manual',
  });

  const [selectedYear, setSelectedYear] = useState<number>(getDefaultYear);
  const [formData, setFormData] = useState<Omit<BusinessMetricsRequest, 'periodStart' | 'periodEnd'>>(getDefaultFormData);

  const handleSubmit = async () => {
    const fullData: BusinessMetricsRequest = {
      ...formData,
      periodStart: `${selectedYear}-01-01`,
      periodEnd: `${selectedYear}-12-31`,
    };
    await onSave(fullData);
  };

  const updateField = <K extends keyof Omit<BusinessMetricsRequest, 'periodStart' | 'periodEnd'>>(
    field: K,
    value: Omit<BusinessMetricsRequest, 'periodStart' | 'periodEnd'>[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className='tw:fixed tw:inset-0 tw:z-50 tw:flex tw:items-center tw:justify-center'>
      <div className='tw:fixed tw:inset-0 tw:bg-black/50' onClick={!isSaving ? onClose : undefined} />
      <div className='tw:relative tw:z-10 tw:bg-background tw:rounded-lg tw:shadow-xl tw:w-full tw:max-w-xl tw:mx-4 tw:max-h-[90vh] tw:flex tw:flex-col'>
        {/* Modal Header */}
        <div className='tw:flex tw:items-center tw:justify-between tw:px-6 tw:py-4 tw:border-b tw:shrink-0'>
          <h5 className='tw:font-semibold tw:mb-0'>{isEdit ? 'Edit Business Metrics' : 'Add Business Metrics'}</h5>
          <Button
            variant='ghost'
            size='icon'
            onClick={onClose}
            disabled={isSaving}
          >
            ×
          </Button>
        </div>

        {/* Modal Body */}
        <div className='tw:px-6 tw:py-4 tw:overflow-y-auto tw:space-y-4'>
          {/* Year Selection */}
          <div>
            <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Reporting Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className={cn(selectClass, 'tw:w-36')}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <p className='tw:text-xs tw:text-muted-foreground tw:mt-1'>Fiscal year (Jan 1 - Dec 31)</p>
          </div>

          {/* Employee Count */}
          <div className='tw:border tw:rounded-lg tw:p-4'>
            <h5 className='tw:font-semibold tw:mb-3 tw:flex tw:items-center tw:gap-2 tw:text-sm'>
              <Users className='tw:size-4 tw:text-primary' />
              Employee Data
            </h5>
            <div className='tw:grid tw:grid-cols-2 tw:gap-3'>
              <div>
                <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Employee Count</label>
                <input
                  type='number'
                  value={formData.employeeCount ?? ''}
                  onChange={(e) =>
                    updateField('employeeCount', e.target.value ? Number(e.target.value) : undefined)
                  }
                  placeholder='e.g., 150'
                  min={0}
                  className={inputClass}
                />
              </div>
              <div>
                <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Count Type</label>
                <select
                  value={formData.employeeCountType ?? ''}
                  onChange={(e) => updateField('employeeCountType', e.target.value as EmployeeCountType)}
                  className={cn(selectClass, 'tw:w-full')}
                >
                  <option value={EmployeeCountType.FTE}>Full-Time Equivalent (FTE)</option>
                  <option value={EmployeeCountType.Headcount}>Headcount</option>
                </select>
              </div>
            </div>
          </div>

          {/* Revenue */}
          <div className='tw:border tw:rounded-lg tw:p-4'>
            <h5 className='tw:font-semibold tw:mb-3 tw:flex tw:items-center tw:gap-2 tw:text-sm'>
              <DollarSign className='tw:size-4 tw:text-emerald-600' />
              Revenue Data
            </h5>
            <div className='tw:grid tw:grid-cols-2 tw:gap-3'>
              <div>
                <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Annual Revenue</label>
                <input
                  type='number'
                  value={formData.revenue ?? ''}
                  onChange={(e) =>
                    updateField('revenue', e.target.value ? Number(e.target.value) : undefined)
                  }
                  placeholder='e.g., 25000000'
                  min={0}
                  className={inputClass}
                />
                <p className='tw:text-xs tw:text-muted-foreground tw:mt-1'>Whole currency units (e.g., 25000000 for $25M)</p>
              </div>
              <div>
                <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Currency</label>
                <select
                  value={formData.revenueCurrency ?? ''}
                  onChange={(e) => updateField('revenueCurrency', e.target.value)}
                  className={cn(selectClass, 'tw:w-full')}
                >
                  <option value='USD'>USD ($)</option>
                  <option value='EUR'>EUR (€)</option>
                  <option value='GBP'>GBP (£)</option>
                  <option value='AED'>AED (د.إ)</option>
                  <option value='AUD'>AUD ($)</option>
                  <option value='SGD'>SGD ($)</option>
                  <option value='JPY'>JPY (¥)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Floor Area */}
          <div className='tw:border tw:rounded-lg tw:p-4'>
            <h5 className='tw:font-semibold tw:mb-3 tw:flex tw:items-center tw:gap-2 tw:text-sm'>
              <Building2 className='tw:size-4 tw:text-blue-600' />
              Floor Area Data
            </h5>
            <div className='tw:grid tw:grid-cols-2 tw:gap-3'>
              <div>
                <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Total Floor Area</label>
                <input
                  type='number'
                  value={formData.floorArea ?? ''}
                  onChange={(e) =>
                    updateField('floorArea', e.target.value ? Number(e.target.value) : undefined)
                  }
                  placeholder='e.g., 5000'
                  min={0}
                  className={inputClass}
                />
              </div>
              <div>
                <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Unit</label>
                <select
                  value={formData.floorAreaUnit ?? ''}
                  onChange={(e) => updateField('floorAreaUnit', e.target.value as FloorAreaUnit)}
                  className={cn(selectClass, 'tw:w-full')}
                >
                  <option value={FloorAreaUnit.SquareMeters}>Square Meters (m²)</option>
                  <option value={FloorAreaUnit.SquareFeet}>Square Feet (ft²)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Notes (Optional)</label>
            <textarea
              value={formData.notes ?? ''}
              onChange={(e) => updateField('notes', e.target.value)}
              placeholder='Add any relevant notes about this data...'
              rows={2}
              className='tw:flex tw:w-full tw:rounded-md tw:border tw:border-input tw:bg-transparent tw:px-3 tw:py-2 tw:text-sm tw:shadow-sm tw:outline-none focus-visible:tw:ring-1 focus-visible:tw:ring-ring tw:resize-none'
            />
          </div>
        </div>

        {/* Modal Footer */}
        <div className='tw:flex tw:justify-end tw:gap-2 tw:px-6 tw:py-4 tw:border-t tw:shrink-0'>
          <Button variant='outline' onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSaving}
            className='tw:bg-emerald-800 tw:hover:bg-emerald-900 tw:text-white tw:border-0'
          >
            {isSaving ? (
              <>
                <Loader2 className='tw:size-4 tw:animate-spin tw:mr-2' />
                Saving...
              </>
            ) : (
              <>
                <Save className='tw:size-4' />
                {isEdit ? 'Save Changes' : 'Add Metrics'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const EmissionsBusinessMetricsPage = () => {
  const history = useHistory();
  const { initiativeId } = useParams<{ initiativeId: string }>();

  const { data: metricsList, isLoading, error } = useGetBusinessMetricsListQuery({ initiativeId });
  const [updateMetrics, { isLoading: isSaving }] = useUpdateBusinessMetricsMutation();
  const [deleteMetrics] = useDeleteBusinessMetricsMutation();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingMetrics, setEditingMetrics] = useState<BusinessMetrics | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handleBack = () => {
    history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ADMIN, { initiativeId }));
  };

  const handleOpenForm = (metrics?: BusinessMetrics) => {
    setEditingMetrics(metrics ?? null);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingMetrics(null);
  };

  const handleSave = async (data: BusinessMetricsRequest) => {
    await updateMetrics({ initiativeId, data });
    handleCloseForm();
  };

  const handleDelete = async (metricsId: string) => {
    await deleteMetrics({ initiativeId, metricsId });
    setDeleteConfirmId(null);
  };

  return (
    <Dashboard>
      {/* Page Header */}
      <div className='tw:flex tw:items-center tw:justify-between tw:mb-6'>
        <div className='tw:flex tw:items-center tw:gap-3'>
          <Button variant='ghost' size='icon' onClick={handleBack} className='tw:text-muted-foreground'>
            <ArrowLeft className='tw:size-4' />
          </Button>
          <div>
            <h4 className='tw:text-xl tw:font-semibold tw:mb-1'>Business Metrics</h4>
            <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
              Configure employee count, revenue, and floor area for carbon intensity calculations
            </p>
          </div>
        </div>
        <Button
          onClick={() => handleOpenForm()}
          className='tw:bg-emerald-800 tw:hover:bg-emerald-900 tw:text-white tw:border-0'
        >
          Add Metrics
        </Button>
      </div>

      {/* Info Card */}
      <div className='tw:flex tw:gap-3 tw:rounded-lg tw:border tw:border-blue-200 tw:bg-blue-50 tw:p-4 tw:text-blue-800 tw:mb-6 tw:text-sm'>
        <Info className='tw:size-4 tw:shrink-0 tw:mt-0.5' />
        <div>
          <strong>About Business Metrics</strong>
          <p className='tw:mb-0 tw:mt-0.5'>
            Business metrics are used to calculate carbon intensity ratios (emissions per employee,
            per revenue, per floor area). You can add metrics for different periods to track how your
            intensity changes over time.
          </p>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className='tw:flex tw:justify-center tw:py-12'>
          <Loader2 className='tw:size-6 tw:animate-spin tw:text-muted-foreground' />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className='tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-4 tw:text-red-800 tw:text-sm tw:mb-4'>
          Failed to load business metrics. Please try again.
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && (!metricsList || metricsList.length === 0) && (
        <Card>
          <CardContent className='tw:p-10 tw:text-center'>
            <div className='tw:flex tw:justify-center tw:mb-4'>
              <PieChart className='tw:size-12 tw:text-muted-foreground' />
            </div>
            <h5 className='tw:font-semibold tw:mb-2'>No Business Metrics Configured</h5>
            <p className='tw:text-sm tw:text-muted-foreground tw:mb-6'>
              Add your organization&apos;s business metrics to calculate carbon intensity ratios
              and track efficiency improvements over time.
            </p>
            <Button
              onClick={() => handleOpenForm()}
              className='tw:bg-emerald-800 tw:hover:bg-emerald-900 tw:text-white tw:border-0'
            >
              Add Your First Metrics
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Metrics List */}
      {!isLoading && !error && metricsList && metricsList.length > 0 && (
        <Card>
          <CardContent className='tw:p-0'>
            <div className='tw:overflow-x-auto'>
              <table className='tw:w-full tw:text-sm'>
                <thead className='tw:bg-muted/50'>
                  <tr>
                    <th className='tw:text-left tw:px-4 tw:py-3 tw:font-medium'>Year</th>
                    <th className='tw:text-left tw:px-4 tw:py-3 tw:font-medium'>Employees</th>
                    <th className='tw:text-left tw:px-4 tw:py-3 tw:font-medium'>Revenue</th>
                    <th className='tw:text-left tw:px-4 tw:py-3 tw:font-medium'>Floor Area</th>
                    <th className='tw:text-left tw:px-4 tw:py-3 tw:font-medium'>Source</th>
                    <th className='tw:text-right tw:px-4 tw:py-3 tw:font-medium'>Actions</th>
                  </tr>
                </thead>
                <tbody className='tw:divide-y'>
                  {metricsList.map((metrics) => (
                    <tr key={metrics._id} className='tw:hover:bg-muted/30'>
                      <td className='tw:px-4 tw:py-3'>
                        <span className='tw:font-medium'>{getYear(metrics.periodStart)}</span>
                        {metrics.notes && (
                          <span className='tw:block tw:text-xs tw:text-muted-foreground'>{metrics.notes}</span>
                        )}
                      </td>
                      <td className='tw:px-4 tw:py-3'>
                        <span>{formatNumber(metrics.employeeCount)}</span>
                        {metrics.employeeCountType && (
                          <span className='tw:block tw:text-xs tw:text-muted-foreground'>
                            {metrics.employeeCountType === EmployeeCountType.FTE ? 'FTE' : 'Headcount'}
                          </span>
                        )}
                      </td>
                      <td className='tw:px-4 tw:py-3'>{formatRevenue(metrics.revenue, metrics.revenueCurrency)}</td>
                      <td className='tw:px-4 tw:py-3'>{formatFloorArea(metrics.floorArea, metrics.floorAreaUnit)}</td>
                      <td className='tw:px-4 tw:py-3'>
                        {metrics.dataSource === 'sample' ? (
                          <Badge className='tw:bg-amber-500/10 tw:text-amber-700 tw:border-amber-200'>Sample</Badge>
                        ) : (
                          <Badge className='tw:bg-emerald-500/10 tw:text-emerald-700 tw:border-emerald-200'>Manual</Badge>
                        )}
                      </td>
                      <td className='tw:px-4 tw:py-3 tw:text-right'>
                        <Button
                          variant='ghost'
                          size='icon'
                          onClick={() => handleOpenForm(metrics)}
                        >
                          <Pencil className='tw:size-4' />
                        </Button>
                        <Button
                          variant='ghost'
                          size='icon'
                          onClick={() => setDeleteConfirmId(metrics._id)}
                          className='tw:text-destructive tw:hover:text-destructive/80'
                        >
                          <Trash2 className='tw:size-4' />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Form Modal */}
      <MetricsForm
        key={editingMetrics?._id ?? 'create'}
        metrics={editingMetrics}
        existingYears={metricsList?.map((m) => getYear(m.periodStart)) ?? []}
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSave={handleSave}
        isSaving={isSaving}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className='tw:fixed tw:inset-0 tw:z-50 tw:flex tw:items-center tw:justify-center'>
          <div className='tw:fixed tw:inset-0 tw:bg-black/50' onClick={() => setDeleteConfirmId(null)} />
          <div className='tw:relative tw:z-10 tw:bg-background tw:rounded-lg tw:shadow-xl tw:w-full tw:max-w-sm tw:mx-4'>
            <div className='tw:flex tw:items-center tw:justify-between tw:px-6 tw:py-4 tw:border-b'>
              <h5 className='tw:font-semibold tw:mb-0'>Confirm Delete</h5>
              <Button
                variant='ghost'
                size='icon'
                onClick={() => setDeleteConfirmId(null)}
              >
                ×
              </Button>
            </div>
            <div className='tw:px-6 tw:py-4 tw:text-sm'>
              Are you sure you want to delete this business metrics record? This action cannot be undone.
            </div>
            <div className='tw:flex tw:justify-end tw:gap-2 tw:px-6 tw:py-4 tw:border-t'>
              <Button variant='outline' onClick={() => setDeleteConfirmId(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
                className='tw:bg-red-600 tw:hover:bg-red-700 tw:text-white tw:border-0'
              >
                <Trash2 className='tw:size-4' />
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </Dashboard>
  );
};
